/*! Kind Paws Home - kp-video.js. Vanilla, no dependencies. Loaded with `defer` by snippets/product-gallery.liquid,
   only on products that have a video. Defines <kp-video> (styles live in that snippet's stylesheet).

   Markup:
     <kp-video class="kp-video" data-kind="video|external" data-label-play="…" data-label-pause="…"
               [data-src-small="…mp4" data-type-small="video/mp4"]>
       <img class="kp-video__poster" loading="lazy">     poster frame (media.preview_image)
       <button data-kp-video-toggle hidden>              play / pause, shown by this script
       <template data-kp-video-template>                 {{ media | video_tag }} or {{ media | external_video_tag }}
     </kp-video>

   Nothing inside the <template> downloads until it is cloned, so no video bytes load with the page.
   - Auto mode (Shopify videos): once the page has finished loading, the video is cloned the first time at least half of
     it is on screen (and, inside a kp-gallery, its slide is the active one). It plays muted and looping, and pauses when
     it leaves the screen, its slide, or the tab. The 44px button pauses it until it is pressed again (WCAG 2.2.2).
   - Manual mode (prefers-reduced-motion: reduce, Save-Data, YouTube / Vimeo, or the browser blocked autoplay): it never
     starts by itself. A play button loads it on tap, with the browser's own controls (or the YouTube / Vimeo player).
   CSS hooks: data-mode="auto|manual", .is-ready (first frame in), .is-playing, .has-controls. */
(() => {
'use strict';
const w = window, d = document;
if (!w.customElements || customElements.get('kp-video')) return;

const query = (q) => (w.matchMedia ? w.matchMedia(q) : null);
const reduceMq = query('(prefers-reduced-motion: reduce)');
const reduced = () => !!(reduceMq && reduceMq.matches);
const saveData = () => !!(navigator.connection && navigator.connection.saveData);
const phone = () => !!(query('(max-width: 899px)') || {}).matches;
const watch = (mq, fn, on) => {
  if (!mq) return;
  if (mq.addEventListener) mq[on ? 'addEventListener' : 'removeEventListener']('change', fn);
  else if (mq.addListener) mq[on ? 'addListener' : 'removeListener'](fn);
};
/* After the load event, so a video never competes with the page's own files */
const afterLoad = (fn) => { if (d.readyState === 'complete') fn(); else w.addEventListener('load', fn, { once: true }); };

class KPVideo extends HTMLElement {
  connectedCallback() {
    this.tpl = this.querySelector('template[data-kp-video-template]');
    if (!this.tpl) return;
    this.btn = this.querySelector('[data-kp-video-toggle]');
    this.poster = this.querySelector('.kp-video__poster');
    this.slide = this.closest('[data-kp-slide]');
    this.external = this.dataset.kind === 'external';
    if (!this.dataset.mode) this.setMode(this.external || reduced() || saveData() || !w.IntersectionObserver ? 'manual' : 'auto');

    if (this.btn) {
      if (!this.wired) {
        this.wired = true;
        this.btn.addEventListener('click', (e) => { e.preventDefault(); this.toggle(); });
      }
      this.btn.hidden = false;
    }

    this.onCheck = this.onCheck || (() => this.check());
    /* Turning on Reduce motion while a video plays stops it at once and switches to a play button */
    this.onReduce = this.onReduce || (() => { if (reduced() && this.dataset.mode === 'auto') { this.pause(); this.setMode('manual'); } });
    d.addEventListener('visibilitychange', this.onCheck);
    watch(reduceMq, this.onReduce, true);
    /* kp-gallery marks the shown slide with .is-active */
    if (this.slide && w.MutationObserver && !this.mo) {
      this.mo = new MutationObserver(this.onCheck);
      this.mo.observe(this.slide, { attributes: true, attributeFilter: ['class'] });
    }
    this.syncFocus();

    if (w.IntersectionObserver) afterLoad(() => {
      if (!this.isConnected || this.io) return;
      /* Slides scrolled out of the gallery track are clipped by it, so they count as off screen */
      this.io = new IntersectionObserver((entries) => {
        const en = entries[entries.length - 1];
        this.ratio = en.isIntersecting ? en.intersectionRatio : 0;
        this.check();
      }, { threshold: [0, 0.5] });
      this.io.observe(this);
    });
  }

  disconnectedCallback() {
    if (this.io) { this.io.disconnect(); this.io = null; }
    if (this.mo) { this.mo.disconnect(); this.mo = null; }
    if (this.onCheck) d.removeEventListener('visibilitychange', this.onCheck);
    if (this.onReduce) watch(reduceMq, this.onReduce, false);
    this.pause();
  }

  /* True outside a gallery, when the slide is the active one, or before kp-gallery has marked any slide */
  get active() {
    const s = this.slide;
    if (!s || s.classList.contains('is-active')) return true;
    return !(s.parentElement && s.parentElement.querySelector(':scope > .is-active'));
  }

  setMode(mode) {
    this.dataset.mode = mode;
    this.label(false);
  }

  label(playing) {
    if (this.btn) this.btn.setAttribute('aria-label', playing ? this.dataset.labelPause || 'Pause video' : this.dataset.labelPlay || 'Play video');
  }

  /* The button (and a video with controls) leave the tab order while their gallery slide is not shown */
  syncFocus() {
    if (!this.slide) return;
    const t = this.active ? 0 : -1;
    if (this.btn) this.btn.tabIndex = t;
    if (this.media && this.classList.contains('has-controls')) this.media.tabIndex = t;
  }

  check() {
    this.syncFocus();
    if (this.external) {
      /* A YouTube / Vimeo player can't be paused from here: remove it once its slide is swiped away or it is off screen */
      if (this.media && (!this.active || this.ratio === 0)) this.reset();
      return;
    }
    /* The ratio only updates when it crosses 0 or 0.5, so a slide left edge-to-edge with the view keeps its last value
       (just under 0.5): compare with exactly 0.5 */
    if (!(this.ratio >= 0.5 && this.active && !d.hidden)) { this.pause(); return; }
    if (this.dataset.mode === 'auto' && !this.userPaused) this.play();
  }

  /* Clones the template once. Returns the <video> or <iframe>. */
  load() {
    if (this.media) return this.media;
    const frag = this.tpl.content.cloneNode(true); /* still inert: nothing downloads until it is in the page */
    const m = frag.querySelector('video, iframe');
    if (!m) return null;
    if (m.tagName === 'VIDEO') {
      m.querySelectorAll('img').forEach((i) => i.remove()); /* video_tag's fallback <img> would download a second poster */
      m.removeAttribute('autoplay');
      m.muted = true;
      m.defaultMuted = true;
      m.playsInline = true;
      m.loop = true;
      const small = this.dataset.srcSmall;
      if (small && phone()) {
        m.removeAttribute('src');
        m.querySelectorAll('source').forEach((s) => s.remove());
        const s = d.createElement('source');
        s.src = small;
        if (this.dataset.typeSmall) s.type = this.dataset.typeSmall;
        m.appendChild(s);
      }
      /* Reuse the poster already on screen (no second download), or none at all */
      const shown = this.poster && this.poster.currentSrc;
      if (shown) m.setAttribute('poster', shown); else m.removeAttribute('poster');
      m.addEventListener('loadeddata', () => this.classList.add('is-ready'), { once: true });
      m.addEventListener('play', () => { this.classList.add('is-playing'); this.label(true); });
      m.addEventListener('pause', () => { this.classList.remove('is-playing'); this.label(false); });
      /* With controls, the arrow keys seek the video instead of changing the gallery slide */
      m.addEventListener('keydown', (e) => { if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') e.stopPropagation(); });
    } else {
      m.addEventListener('load', () => this.classList.add('is-ready'), { once: true });
    }
    this.insertBefore(frag, this.btn || null);
    this.media = m;
    return m;
  }

  play() {
    const v = this.load();
    if (!v || v.tagName !== 'VIDEO' || !v.paused) return;
    v.muted = true;
    const p = v.play();
    /* Autoplay blocked (e.g. Low Power Mode): wait for a tap instead. A pause() before playback began is not an error. */
    if (p && p.catch) p.catch((err) => { if (err && err.name === 'NotAllowedError' && this.dataset.mode === 'auto') this.setMode('manual'); });
  }

  pause() {
    const v = this.media;
    if (v && v.tagName === 'VIDEO' && !v.paused) v.pause();
  }

  toggle() {
    if (this.dataset.mode === 'manual') { this.start(); return; }
    const v = this.media;
    if (v && !v.paused) { this.userPaused = true; this.pause(); }
    else { this.userPaused = false; this.play(); }
  }

  /* Manual mode: a tap loads the video with the browser's own controls (or the YouTube / Vimeo player, which starts
     itself through autoplay=1 in its URL) and moves focus to it */
  start() {
    const m = this.load();
    if (!m) return;
    this.classList.add('has-controls');
    m.tabIndex = 0;
    if (m.tagName === 'VIDEO') {
      m.controls = true;
      this.classList.add('is-ready');
      const p = m.play();
      if (p && p.catch) p.catch(() => {});
    }
    try { m.focus({ preventScroll: true }); } catch (e) { m.focus(); }
  }

  reset() {
    if (this.media) this.media.remove();
    this.media = null;
    this.classList.remove('is-ready', 'is-playing', 'has-controls');
    this.label(false);
  }
}

customElements.define('kp-video', KPVideo);
})();
